<template>
    <section class="gtaf-blog-body">

        <!-- ================= Category Tabs with Arrows ================= -->
        <div class="tabs-wrapper">
            <button v-if="showLeftArrow" class="arrow-btn left" @click="scrollTabs('left')">‹</button>

            <div class="blog-tabs" ref="tabsContainer" @scroll="updateArrowVisibility">
                <button
                        v-for="cat in allCategoriesData"
                        :key="cat.requestKey"
                        :class="['tab-btn', selectedCategory === cat.requestKey ? 'active' : '']"
                        @click="selectedCategory = cat.requestKey"
                >
                    {{ cat.category }}
                </button>
            </div>

            <button v-if="showRightArrow" class="arrow-btn right" @click="scrollTabs('right')">›</button>
        </div>

        <h1 class="text-center fw-bold mb-4">Recent Blogs</h1>

        <b-row>
            <!-- First Blog (large left post) -->
            <b-col sm="6" class="mt-2"
                   v-for="content in activePosts.slice(0, 1)"
                   :key="content.requestKey"
                   @click="goToDetail(content.requestKey)"
                   style="cursor: pointer;">
                <img :src="getImagePath() + content.imageUrl" width="600" height="300" alt=""
                     style="object-fit: cover; width: 100%;"/>
                <h3 class="text-start mt-3">{{ content.contentTitle }}</h3>
                <p class="text-start">{{ content.summary }}</p>
                <p class="text-start">{{ content.category }} | <span>{{ formatDate(content.publishDate) }}</span></p>
            </b-col>

            <!-- Sidebar right (3 posts) -->
            <b-col sm="6" class="mt-2">
                <b-row v-for="contentList in activePosts.slice(1, 4)"
                       :key="contentList.requestKey"
                       @click="goToDetail(contentList.requestKey)"
                       style="cursor: pointer;">
                    <b-col sm="6" class="mt-4">
                        <h5 class="text-start fw-bold">{{ contentList.contentTitle }}</h5>
                        <p class="text-start mt-5">{{ contentList.category }} | <span>{{ formatDate(contentList.publishDate) }}</span></p>
                    </b-col>
                    <b-col sm="6" class="mt-4">
                        <img :src="getImagePath() + contentList.imageUrl" width="280" height="160" alt=""/>
                    </b-col>
                </b-row>
            </b-col>
            <hr/>
        </b-row>

        <!-- All posts grid -->
        <b-row>
            <b-col sm="4" v-for="allContent in visiblePosts" :key="allContent.requestKey"
                   class="d-flex flex-column"
                   @click="goToDetail(allContent.requestKey)"
                   style="cursor: pointer;">
                <div style="width: 390px; height: 260px; overflow: hidden;">
                    <img :src="getImagePath() + allContent.imageUrl"
                         class="mt-4" alt=""
                         style="width: 100%; height: 100%; object-fit: cover;"/>
                </div>
                <h5 class="text-start mt-4"
                    style="min-height: 60px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                    {{ allContent.contentTitle }}
                </h5>
                <p class="text-start mt-2">
                    {{ allContent.category }} | <span>{{ formatDate(allContent.publishDate) }}</span>
                </p>
            </b-col>
        </b-row>

        <!-- Show More Button -->
        <div class="load-more-wrapper text-center mt-4" v-if="hasMore">
            <button @click="loadMore" class="load-more-btn">Show More</button>
        </div>

    </section>
</template>

<script>
    import apiRepository, {getImage} from "@/services/apiRepository";

    export default {
        name: "BlogList",
        data() {
            return {
                lang: null,
                allCategoriesData: [
                    {requestKey: null, category: 'All'}
                ],
                selectedCategory: null,
                visibleCount: 3,
                recentPostData: [],
                ctgWiseBlogs: [],
                showLeftArrow: false,
                showRightArrow: false,
            };
        },

        computed: {
            // currently active post list based on selected category
            activePosts() {
                return this.selectedCategory === null ? this.recentPostData : this.ctgWiseBlogs;
            },
            visiblePosts() {
                return this.activePosts.slice(0, this.visibleCount);
            },
            hasMore() {
                return this.visibleCount < this.activePosts.length;
            }
        },

        watch: {
            selectedCategory(newVal) {
                this.visibleCount = 3;
                this.ctgWiseBlogs = [];
                this.relatedPosts(newVal);
            }
        },

        mounted() {
            this.langData();
            this.getAllCategories();
            this.loadRecentPosts();
            this.relatedPosts(null);
        },

        beforeUnmount() {
            window.removeEventListener("resize", this.updateArrowVisibility);
        },

        methods: {
            langData() {
                this.lang = localStorage.getItem("locale");
            },

            loadMore() {
                this.visibleCount += 6;
            },

            goToDetail(requestKey) {
                this.$router.push({name: 'BlogDetails', params: {id: requestKey}});
            },

            getImagePath() {
                return getImage();
            },

            formatDate(dateStr) {
                if (!dateStr) return '';
                return new Date(dateStr).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                });
            },

            loadRecentPosts() {
                apiRepository.callApi(apiRepository.GET_COMMAND, `/getRecentBlogContents`).then(res => {
                    if (res) this.recentPostData = res.data;
                });
            },

            relatedPosts(id) {
                const url = id
                    ? `/getBlogContents?category=${id}&lang=${this.lang}`
                    : `/getBlogContents?lang=${this.lang}`;

                apiRepository.callApi(apiRepository.GET_COMMAND, url).then(res => {
                    if (res) this.ctgWiseBlogs = res.data;
                }).catch(err => {
                    console.error(err.response?.data || err.message);
                });
            },

            getAllCategories() {
                apiRepository.callApi(apiRepository.GET_COMMAND, `/lookup/blogCategoryList?lang=${this.lang}`)
                    .then((res) => {
                        if (res && res.data.length > 0) {
                            this.allCategoriesData = [
                                {requestKey: null, category: 'All'},
                                ...res.data
                            ];
                            this.selectedCategory = null;

                            // ✅ Check arrows AFTER categories are rendered
                            this.$nextTick(() => {
                                this.updateArrowVisibility();
                                window.addEventListener("resize", this.updateArrowVisibility);
                            });
                        }
                    }).catch(error => {
                    console.error(error.response?.data || error.message);
                });
            },

            scrollTabs(direction) {
                const container = this.$refs.tabsContainer;
                if (!container) return;
                container.scrollLeft += direction === 'left' ? -220 : 220;
                this.$nextTick(() => this.updateArrowVisibility());
            },

            // ✅ Called on: scroll event, after categories load, on resize
            updateArrowVisibility() {
                const container = this.$refs.tabsContainer;
                if (!container) return;
                this.showLeftArrow = container.scrollLeft > 0;
                this.showRightArrow = container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
            },
        },
    };
</script>

<style scoped>
    .gtaf-blog-body {
        max-width: 1280px;
        margin: auto;
        padding: 40px 20px 80px;
        background: #ffffff;
        font-family: "Inter", sans-serif;
    }

    /* ================= Tabs with Arrows ================= */
    .tabs-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 50px;
    }

    .blog-tabs {
        display: flex;
        gap: 12px;
        overflow-x: auto;
        scroll-behavior: smooth;
        padding: 6px 10px;
        max-width: 100%;
        scrollbar-width: none;
    }

    .blog-tabs::-webkit-scrollbar {
        display: none;
    }

    .tab-btn {
        white-space: nowrap;
        padding: 8px 18px;
        border-radius: 999px;
        border: 1px solid #e5e7eb;
        background: #ffffff;
        color: #374151;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s ease;
    }

    .tab-btn.active {
        background: #0f766e;
        color: #ffffff;
        border-color: #0f766e;
    }

    .arrow-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background: #ffffff;
        border: 1px solid #e5e7eb;
        color: #111827;
        font-size: 20px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        cursor: pointer;
        z-index: 10;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .arrow-btn.left  { left: -18px; }
    .arrow-btn.right { right: -18px; }

    .arrow-btn:hover { background: #f9fafb; }

    /* ================= Load More ================= */
    .load-more-wrapper { text-align: center; margin-top: 60px; }

    .load-more-btn {
        padding: 14px 32px;
        border-radius: 999px;
        background: #0f766e;
        color: #ffffff;
        font-weight: 700;
        border: none;
        cursor: pointer;
        transition: background 0.2s ease;
    }

    .load-more-btn:hover { background: #115e59; }
</style>